import { createContext, useContext } from 'react'
import type { FsKind } from '../transport/api'
import type { YouTubeVideo } from '../ui/youtubeLink'

/**
 * HOW A PICTURE IN A TRANSCRIPT REACHES THE COVER-PANEL VIEWER.
 *
 * App owns the viewer (#fileView, FileViewer) and the `openDoc` that opens it. The
 * pictures that want it sit five components down inside Clean View (AgentPanel → the
 * turn renderers → Msg → MsgBody → the image tiles), and the project chat already
 * threads the same callback as a prop through four signatures. This is the first
 * context in the codebase, and it is here because the alternative was that prop,
 * written into eight more signatures for one callback that never changes.
 *
 * Owner, 2026-09-13: 在 Agent Chat 的 Clean View 裡面, 點下任何已經上傳的圖片, 它都會自動
 * 下載. 點下圖片的行為需要是讓它成為一個蓋板的屏幕, 讓使用者可以看到放大的圖案 — 上面有一個
 * X 取消按鈕, 在 X 按鈕的左邊放置一個 Download 按鈕. That screen already existed for the
 * project chat (FileViewer — the 2026-08-14 cover panel, whose header puts Download
 * immediately left of Close); Clean View's pictures simply had no road to it and fell
 * back to `target="_blank"` on an object URL, which WebKit, handed a blob with no
 * type, SAVES instead of showing. That is the download the owner saw.
 *
 * `null` means no viewer is mounted above — a test rendering one block, a surface
 * without one — and the tile falls back to the tab it always opened.
 */
export type OpenDoc = (a: {
  url: string
  name: string
  mime: string
  /**
   * A YouTube link, played in the same cover panel by YouTube's own player (owner,
   * 2026-10-19: 直接使用 in-app browser). `url` is then the link as written.
   */
  youtube?: YouTubeVideo
}) => void

export const OpenDocContext = createContext<OpenDoc | null>(null)

/**
 * WHERE NO VIEWER IS MOUNTED, A FILE IS SAVED — NEVER OPENED AS A PAGE (security audit, 2026-11-05).
 *
 * A blob URL opened in a tab is a document of this origin: an SVG or a web page posted in a room ran its script as
 * app.avengents.ai. So the last-resort opener hands a file to the browser's download (an `<a download>` is not a
 * navigation), and only a web link — someone else's origin — opens a tab.
 */
export const openOutside: OpenDoc = (d) => {
  if (/^https?:\/\//i.test(d.url)) {
    window.open(d.url, '_blank', 'noopener')
    return
  }
  if (!/^blob:/i.test(d.url)) return
  const a = document.createElement('a')
  a.href = d.url
  a.download = d.name || 'file'
  a.rel = 'noopener'
  a.click()
}

/** Which of the viewer's panes a document opens in, by its stated type, else its name. */
export function docKind(a: { name: string; mime: string }): FsKind {
  const m = (a.mime ?? '').split(';')[0] ?? ''
  const lower = a.name.toLowerCase()
  return m.startsWith('image/') ? 'image'
    : m.startsWith('video/') ? 'video'
    : m.startsWith('audio/') ? 'audio'
    : m.endsWith('markdown') || lower.endsWith('.md') ? 'markdown'
    : 'text'
}

export function useOpenDoc(): OpenDoc | null {
  return useContext(OpenDocContext)
}
